import type { HttpError } from '@refinedev/core'
import { useTable } from '@refinedev/react-table'
import type { Game } from '@sirius/contracts/admin'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ListEmptyState } from '~/components/listEmptyState'
import { ShowButton } from '~/components/refineUi/buttons/show'
import { DataTable } from '~/components/refineUi/dataTable/dataTable'
import { DataTableFilterDropdownText } from '~/components/refineUi/dataTable/dataTableFilter'
import { DataTableSorter } from '~/components/refineUi/dataTable/dataTableSorter'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { versionLabel } from './paths'

/**
 * The platform's games (staff only). Games come with releases: staff edit
 * a game's config as a new version on its show page.
 */
export function GameList() {
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<Game>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column, table }) => (
          <div className="flex items-center gap-1">
            {t('gamesPage.list.columns.name')}
            <DataTableSorter column={column} />
            <DataTableFilterDropdownText
              defaultOperator="contains"
              operators={['contains']}
              column={column}
              table={table}
              placeholder={t('gamesPage.list.searchName')}
            />
          </div>
        ),
      },
      {
        id: 'slug',
        accessorKey: 'slug',
        header: t('gamesPage.list.columns.slug'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'kind',
        accessorKey: 'kind',
        header: t('gamesPage.list.columns.kind'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: 'defaultConfiguration',
        header: t('gamesPage.list.columns.defaultConfiguration'),
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) =>
          versionLabel(
            row.original.defaultConfigurationName,
            row.original.configVersion,
          ),
      },
      {
        id: 'actions',
        header: '',
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <ShowButton
              recordItemId={row.original.id}
              size="sm"
            />
          </div>
        ),
      },
    ],
    [t],
  )

  const table = useTable<Game, HttpError>({
    columns,
    refineCoreProps: {
      resource: 'games',
      syncWithLocation: true,
      pagination: { pageSize: 20 },
      sorters: { initial: [{ field: 'name', order: 'asc' }] },
    },
  })
  const { tableQuery, filters } = table.refineCore
  const isEmpty = tableQuery.data?.total === 0

  return (
    <ListView>
      <ListViewHeader />
      {isEmpty ? (
        <ListEmptyState
          resource="games"
          label={t('gamesPage.list.emptyLabel')}
          canCreate={false}
          hasFilters={filters.length > 0}
          // via the column filters, so react-table and Refine stay in sync
          onClearFilters={() => table.reactTable.setColumnFilters([])}
        />
      ) : (
        <DataTable table={table} />
      )}
    </ListView>
  )
}
